import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Wallet, Calendar, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function ReviewDialog({ bookingId, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { booking_id: bookingId, rating, comment });
      toast.success("Review submitted");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`open-review-${bookingId}`}>Leave review</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">How was your session?</DialogTitle></DialogHeader>
        <div className="flex gap-1 my-4">
          {[1,2,3,4,5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
              <Star className={`h-8 w-8 ${n <= rating ? "fill-[#2DD4BF] text-[#2DD4BF]" : "text-[#142542]"}`} />
            </button>
          ))}
        </div>
        <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What went well? What could be better?" data-testid="review-comment" />
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#2DD4BF] text-[#05070C]" data-testid="submit-review">
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DashboardPage() {
  const { user, switchRole } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [prof, setProf] = useState(null);

  const refresh = async () => {
    const { data } = await api.get("/bookings/me");
    setBookings(data);
    if (user?.role === "professional") {
      try { const { data: p } = await api.get("/professionals/me"); setProf(p); } catch { /* ignore */ }
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user?.role]);

  const doComplete = async (id) => {
    try { await api.post(`/bookings/${id}/complete`); toast.success("Marked complete"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const earnings = user?.role === "professional"
    ? bookings.filter((b) => b.status === "completed").reduce((a, b) => a + (b.payout_amount || 0), 0)
    : bookings.filter((b) => b.status !== "cancelled").reduce((a, b) => a + (b.total_amount || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eyebrow text-[#2DD4BF] mb-3">Dashboard</div>
          <h1 className="font-display text-4xl text-[#E8ECF3]">Hi {user?.name?.split(" ")[0]}.</h1>
        </div>
        <div className="hidden md:flex gap-3">
          {user?.role !== "professional" && (
            <Button variant="outline" className="rounded-full" onClick={() => switchRole("professional")} data-testid="switch-pro-btn">Become an expert</Button>
          )}
          {user?.role === "professional" && (
            <Link to="/profile/edit"><Button variant="outline" className="rounded-full" data-testid="edit-profile-btn">Edit profile</Button></Link>
          )}
          <Link to="/professionals"><Button className="rounded-full bg-[#2DD4BF] text-[#05070C]" data-testid="browse-btn">Browse experts</Button></Link>
        </div>
      </div>

      {user?.role === "professional" && prof && (
        <div className="mb-8 p-6 rounded-3xl border border-[#142542] bg-[#0F1B33] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className={`h-5 w-5 ${prof.verified ? "text-[#2DD4BF]" : "text-[#2DD4BF]"}`} />
            <div>
              <div className="font-display text-lg text-[#E8ECF3]">
                {prof.verified ? "You're verified!" : "Verification pending"}
              </div>
              <div className="text-sm text-[#94A3B8]">
                {prof.verified ? "Your profile is live and searchable." : "Our team is reviewing your profile. Usually done in under 24 hours."}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="border border-[#142542] rounded-3xl p-6 bg-[#0F1B33]">
          <div className="label-eyebrow text-[#2DD4BF] mb-2">Bookings</div>
          <div className="flex items-center gap-3">
            <Calendar className="h-6 w-6 text-[#E8ECF3]" />
            <div className="font-display text-3xl text-[#E8ECF3]">{bookings.length}</div>
          </div>
        </div>
        <div className="border border-[#142542] rounded-3xl p-6 bg-[#0F1B33]">
          <div className="label-eyebrow text-[#2DD4BF] mb-2">{user?.role === "professional" ? "Earnings (₹)" : "Spend (₹)"}</div>
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-[#E8ECF3]" />
            <div className="font-display text-3xl text-[#E8ECF3]">{earnings.toLocaleString("en-IN")}</div>
          </div>
        </div>
        <div className="border border-[#142542] rounded-3xl p-6 bg-[#0F1B33]">
          <div className="label-eyebrow text-[#2DD4BF] mb-2">Rating</div>
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 fill-[#2DD4BF] text-[#2DD4BF]" />
            <div className="font-display text-3xl text-[#E8ECF3]">{prof?.avg_rating || "—"}</div>
          </div>
        </div>
      </div>

      <h2 className="font-display text-2xl text-[#E8ECF3] mb-4">Your sessions</h2>
      {bookings.length === 0 ? (
        <div className="border border-dashed border-[#142542] rounded-3xl p-12 text-center">
          <div className="text-[#94A3B8]">No bookings yet.</div>
          <Link to="/professionals"><Button className="mt-4 rounded-full bg-[#2DD4BF] text-white" data-testid="empty-browse-btn">Find an expert</Button></Link>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.booking_id} className="border border-[#142542] rounded-2xl p-5 bg-[#0F1B33] flex flex-wrap items-center gap-4" data-testid={`booking-${b.booking_id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="font-display text-lg text-[#E8ECF3]">
                  {user?.role === "professional" ? b.client_name : b.professional_name}
                </div>
                <div className="text-xs text-[#94A3B8] flex items-center gap-3 mt-1">
                  <span className="capitalize">{b.session_type}</span> · <span>{b.duration_hours}h</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(b.scheduled_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="font-display text-lg">₹{b.total_amount?.toLocaleString("en-IN")}</div>
              <span className={`text-xs px-3 py-1 rounded-full ${
                b.status === "completed" ? "bg-[#2DD4BF] text-white" :
                b.status === "confirmed" ? "bg-[#0F1B33] text-[#E8ECF3]" :
                b.status === "pending_payment" ? "bg-[#2DD4BF] text-white" : "bg-[#05070C] text-[#94A3B8]"
              }`}>
                {b.status === "pending_payment" ? "Payment pending" : b.status}
              </span>
              <div className="flex gap-2">
                {b.status === "confirmed" && (
                  <Button variant="outline" size="sm" onClick={() => doComplete(b.booking_id)} data-testid={`complete-${b.booking_id}`}>Mark complete</Button>
                )}
                {user?.role === "client" && b.status === "completed" && (
                  <ReviewDialog bookingId={b.booking_id} onDone={refresh} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
